/**
 * Ask.
 *
 * The screen is arranged so the evidence is never further away than the
 * answer. Chat elsewhere is a sentence alone in the register of an authority;
 * here the clauses it rests on sit directly beneath it, verbatim, and a
 * refusal gets the same visual weight as an answer rather than reading as an
 * error the user should retry past.
 *
 * A refusal is the interesting output, not the failure case. "Your agreement
 * does not address this" is where a claim comes from, and the two refusals
 * that mean the software caught something — a citation to a clause it was
 * never shown, a claim about another employer's award with nothing behind it —
 * are worth reading in full, because each one is a wrong answer that did not
 * reach anybody.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseAnswer } from './answer.js';
import { createThreadWriter } from './thread.js';
import { api, type AskAnswer, type AskThreadRow } from '../lib/api.js';
import { ClaimFromAnswer } from './ClaimFromAnswer.js';
import { MAX_QUESTION } from '@tmea/shared';

export function Ask({
  seed,
  onOpenClause,
}: {
  /**
   * A passage carried in from Read by the Ask verb.
   *
   * Prefilled rather than asked immediately: the selection is a starting point
   * and the person almost always wants to turn it into a question in their own
   * words first. Sending it as-is would spend a model call on a sentence
   * nobody asked.
   */
  seed?: string | null;
  onOpenClause: (number: string) => void;
}) {
  const [question, setQuestion] = useState(seed ?? '');
  const [history, setHistory] = useState<AskAnswer[]>([]);

  /*
   * The question in flight, held so it can be shown while it is.
   *
   * `send` cleared the input and nothing else changed until the answer came
   * back — and a grounded answer over four instruments takes seconds. So the
   * first question a person ever asks empties the box they typed into and
   * leaves the screen exactly as it was, which does not read as "working", it
   * reads as "did not register". They ask again, and now two model calls are
   * running.
   *
   * Kept separate from `history` rather than pushed into it as a placeholder,
   * because history holds answers and a question with no answer is not one —
   * every reader of that array would need to know about a half-built member.
   */
  const [pending, setPending] = useState<string | null>(null);

  /**
   * The thread, so a second question can mean something.
   *
   * "Are there any provisions for a moving day?" — no, here is every leave type
   * — "what would a clause extending 24.3 look like?" The second question is
   * unanswerable alone, and until now nothing carried the first.
   *
   * Turn ids are held beside the answers rather than inside them, because
   * `AskAnswer` is what the model returned and a turn id is what the database
   * did with it. Merging them would mean every reader of an answer has to know
   * whether it has been persisted yet.
   */
  const [threadId, setThreadId] = useState<string | null>(null);
  const [turnIds, setTurnIds] = useState<Record<string, string>>({});

  /*
   * Which thread a turn belongs to is held in the writer, not in state.
   *
   * Reading it from state meant a second question recorded before the next
   * render saw no thread and opened its own — the rail listed the same
   * question twice, the second entry holding the follow-up. The race is widest
   * after a refusal, which returns at once and is exactly when somebody
   * rephrases immediately.
   */
  const writer = useRef(createThreadWriter({ startThread: api.startThread, addTurn: api.addTurn }));

  const record = useMutation({
    mutationFn: (result: AskAnswer) =>
      writer.current.record({
        question: result.question,
        answer: result.answer,
        citations: result.citations,
        model: result.model,
      }),
    onSuccess: (saved, result) => {
      setThreadId(saved.threadId);
      setTurnIds((prev) => ({ ...prev, [result.question]: saved.turnId }));
      /*
       * Named after the first answer, not before it.
       *
       * A title costs a model call, and the person is waiting for the answer
       * rather than for the rail. The route is idempotent, so calling it on a
       * thread that already has one is a cheap no-op — which is why this can
       * fire on every turn without a guard that would drift out of step with
       * what the server actually did.
       */
      void api
        .nameThread(saved.threadId)
        .catch(() => null)
        .finally(() => {
          void client.invalidateQueries({ queryKey: ['ask-threads'] });
        });
    },
  });

  const submit = useMutation({
    /*
     * The thread comes from the writer, not from state, for the same reason
     * the record does: state is a render behind, and a follow-up asked
     * quickly would be sent as though it opened the conversation.
     */
    mutationFn: (q: string) => api.ask(q, writer.current.current()),
    onSuccess: (result) => {
      setHistory((prev) => [...prev, result]);
      setPending(null);
      /*
       * Recorded after the answer is on screen, not before it. Persisting the
       * turn is what makes it claimable, and it is not what the person is
       * waiting for — a failed write should cost them the claim button, never
       * the answer.
       */
      record.mutate(result);
    },
    // Cleared on failure too. A question stuck under a thinking indicator that
    // will never resolve is worse than the blank screen this replaces.
    onError: () => setPending(null),
  });

  /*
   * Computed on every render rather than held in state: it is a function of the
   * text in the box, and a second copy of that fact is a second thing to keep
   * in step with it.
   */
  const over = question.trim().length - MAX_QUESTION;
  const nearLimit = question.trim().length > MAX_QUESTION * 0.8;

  const send = (q: string): void => {
    const trimmed = q.trim();
    if (trimmed.length < 3) return;
    /*
     * The same limit the button enforces. Enter sends, so the disabled button
     * is not the only way in — and the failure this guards is losing the paste
     * out of the box on a send the server would refuse anyway.
     */
    if (trimmed.length > MAX_QUESTION) return;
    setQuestion('');
    setPending(trimmed);
    submit.mutate(trimmed);
  };

  const bank = useQuery({ queryKey: ['answers'], queryFn: api.answers });
  const instruments = useQuery({ queryKey: ['instruments'], queryFn: api.instruments });

  /*
   * The thread list, and moving between threads.
   *
   * `history` is what is on screen and is rebuilt from the server when a past
   * thread is opened, rather than kept per-thread in memory. Two copies of the
   * same exchange is how one of them goes stale, and the stale one is always
   * the one somebody screenshots.
   */
  const client = useQueryClient();
  const threads = useQuery({ queryKey: ['ask-threads'], queryFn: api.askThreads });

  const startNewThread = (): void => {
    writer.current.reset();
    setThreadId(null);
    setHistory([]);
    setTurnIds({});
    setPending(null);
  };

  const openThread = (id: string): void => {
    writer.current.resume(id);
    setThreadId(id);
    setPending(null);
    void api.askThread(id).then((data) => {
      /*
       * In the order they happened, which is the order the API returns them.
       * A transcript reads downwards; the newest turn is at the bottom because
       * that is where the conversation is up to.
       */
      const turns = [...data.turns];
      setHistory(
        turns.map((t) => ({
          question: t.question,
          answer: t.answer,
          refusal: t.answer === null ? 'This answer was not kept.' : null,
          citations: t.citations,
          /*
           * Evidence cards are deliberately empty on a reopened thread. They
           * are the verbatim clause text as retrieved at the time, and they are
           * not stored on the turn — reconstructing them from today's corpus
           * would show a card that may not be what the answer rested on. The
           * citations are kept and remain clickable.
           */
          evidence: [],
          model: t.model,
        })),
      );
      setTurnIds(Object.fromEntries(turns.map((t) => [t.question, t.id])));
      // After the turns render, not before: scrollHeight is still the old one.
      requestAnimationFrame(() => toFoot('auto'));
    });
  };

  /*
   * The transcript follows the conversation down.
   *
   * It used to stack newest-first, so the answer being read was at the top and
   * the input was at the bottom with the oldest exchange next to it. Every
   * other chat this group uses reads downwards, and the inversion cost a beat
   * of reorientation on every turn.
   *
   * Reading downwards only works if the view moves with it, so the scroller is
   * pinned to the foot whenever a turn is added or one starts. `smooth` on a
   * new question and `auto` when a whole thread is opened: animating a jump of
   * twenty exchanges is a long slide past content nobody asked to see.
   */
  const composer = useRef<HTMLTextAreaElement | null>(null);
  /*
   * Grown to fit what has been typed, rather than left at three lines with a
   * scrollbar. Reset to auto first, or the height only ever ratchets upwards
   * and deleting a paragraph leaves the box the size it was.
   */
  useEffect(() => {
    const el = composer.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [question]);

  const scroller = useRef<HTMLDivElement | null>(null);
  const toFoot = (behavior: ScrollBehavior): void => {
    const el = scroller.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  };
  useEffect(() => {
    toFoot('smooth');
  }, [history.length, pending]);

  /*
   * Attaching a document.
   *
   * A thread is opened on demand, because a file needs somewhere to live and a
   * conversation previously did not exist until the first answer — which is
   * exactly the answer that most needs to have read the document.
   *
   * Uploads are immediate rather than held until send. A 4 MB PDF takes a
   * moment to read and extract, and doing it while somebody is still typing
   * their question is time they do not spend waiting.
   */
  const attach = useMutation({
    mutationFn: async (files: File[]) => {
      let id = writer.current.current();
      if (id === null) {
        const opened = await api.openThread(files[0]?.name ?? 'Attached document');
        writer.current.resume(opened.threadId);
        setThreadId(opened.threadId);
        id = opened.threadId;
      }
      for (const file of files) await api.attach(id, file);
      return id;
    },
    onSuccess: (id) => {
      void client.invalidateQueries({ queryKey: ['ask-attachments', id] });
      void client.invalidateQueries({ queryKey: ['ask-threads'] });
    },
  });

  const attached = useQuery({
    queryKey: ['ask-attachments', threadId],
    queryFn: () => api.attachments(threadId!),
    enabled: threadId !== null,
  });

  const detach = useMutation({
    mutationFn: (id: string) => api.detach(threadId!, id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ask-attachments', threadId] });
    },
  });

  const [dragging, setDragging] = useState(false);

  /*
   * The conversation survives a reload.
   *
   * The rail was listing what looked like a chat per question. It was not —
   * there is a seven-turn thread and two of three in the same account — but
   * every reload silently began a new one, and a tab gets reloaded a great
   * deal: a deploy, a crash, a closed laptop. Somebody mid-exchange came back
   * to an empty column, asked the next question, and it opened a conversation
   * with no memory of the one it was continuing.
   *
   * sessionStorage rather than localStorage, deliberately. It is per tab and
   * dies with the browser session, which is the right lifetime for a pointer
   * into a workspace holding private questions about somebody's pay. Two tabs
   * are then two conversations, which is also what a person means by opening a
   * second tab.
   *
   * The id is a pointer and not the content. If the thread has since been
   * deleted, or belongs to somebody else, the fetch fails and the surface
   * quietly starts fresh rather than showing an error about a thread the
   * reader never chose.
   */
  useEffect(() => {
    const remembered = sessionStorage.getItem('ask-thread');
    if (remembered === null) return;
    void api
      .askThread(remembered)
      .then((data) => {
        if (data.turns.length === 0) return;
        writer.current.resume(remembered);
        setThreadId(remembered);
        setHistory(
          data.turns.map((t) => ({
            question: t.question,
            answer: t.answer,
            refusal: t.answer === null ? 'This answer was not kept.' : null,
            citations: t.citations,
            evidence: [],
            model: t.model,
          })),
        );
        setTurnIds(Object.fromEntries(data.turns.map((t) => [t.question, t.id])));
        requestAnimationFrame(() => toFoot('auto'));
      })
      .catch(() => sessionStorage.removeItem('ask-thread'));
    // Once, on mount. The remembered thread is a starting point, not a
    // subscription — reopening one from the rail is what changes it after that.
  }, []);

  useEffect(() => {
    if (threadId === null) sessionStorage.removeItem('ask-thread');
    else sessionStorage.setItem('ask-thread', threadId);
  }, [threadId]);

  const share = useMutation({
    mutationFn: ({ id, visibility }: { id: string; visibility: 'private' | 'team' }) =>
      api.shareThread(id, visibility),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ask-threads'] });
    },
  });

  return (
    /*
     * The mockup's shape, and the reasons behind it.
     *
     * A rail of grouped questions on the left, an editorial opening in the
     * middle, and the input pinned to the bottom. The old version was a
     * heading, a box and a list of muted links — which asks a teacher who has
     * opened this once, before a meeting, to guess what the tool can do.
     *
     * The rail lives inside this surface rather than in the shell. The mockup
     * swaps the shell's rail per mode — clause tree in Read, questions in Ask,
     * claims in Claims — and that is the right structure, but it is a change
     * to the frame every surface hangs in. This gets the same reading
     * experience without it; the shell-level swap is still worth doing.
     */
    <div className="flex h-full min-h-0">
      <aside
        className="hidden w-[280px] shrink-0 flex-col border-r border-[var(--color-rule)] md:flex"
        aria-label="Things people ask"
      >
        <div className="flex items-baseline justify-between gap-2 border-b border-[var(--color-rule)] px-3 py-3">
          <div className="min-w-0">
            <h2 className="mb-0.5 text-[12.5px] font-semibold">Ask</h2>
            <p className="text-[11.5px] leading-snug text-[var(--color-muted)]">
              Answers come only from your agreement, the Award, and the NSW public Award.
            </p>
          </div>
          {/* New chat is a button, not a link: it clears state rather than
              navigating, and a thread is not created until a question is
              answered — an empty thread in the list is a thread nobody asked. */}
          <button
            type="button"
            onClick={startNewThread}
            className="hit label shrink-0 rounded-[6px] border border-[var(--color-rule-strong)] px-2 py-1"
          >
            New
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <ThreadRail
            threads={threads.data?.threads ?? []}
            activeId={threadId}
            onOpen={openThread}
            onShare={(id, visibility) => share.mutate({ id, visibility })}
          />

          {(bank.data?.groups ?? []).map((group) => (
            <div key={group.label} className="border-b border-[var(--color-rule)] px-3 py-2.5">
              <p className="label mb-1.5">{group.label}</p>
              <div className="flex flex-col gap-1.5">
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => send(item.question)}
                    className="hit rounded-[6px] border border-[var(--color-rule)] px-2.5 py-2 text-left text-[11.5px] leading-snug"
                  >
                    {item.question}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto px-6 py-8">
          {history.length === 0 && !submit.isPending ? (
            <div className="mx-auto max-w-[680px]">
              {/* Editorial, not a form label. This is the one screen where a
                  sentence of orientation is worth more than density. */}
              <h1 className="mb-4 text-[34px] font-semibold leading-[1.1] tracking-tight">
                Ask anything about
                <br />
                your agreement.
              </h1>
              <p className="measure-wide mb-6 text-[15px] leading-relaxed">
                Type a question the way you&rsquo;d say it out loud. Every answer quotes the clause
                it came from, and says plainly when the agreement doesn&rsquo;t cover something —
                which turns out to matter more than what it does say.
              </p>

              <div className="mb-2 border-t border-[var(--color-rule)] pt-5">
                <div className="flex flex-col gap-2.5">
                  {(bank.data?.groups.flatMap((g) => g.items).slice(0, 3) ?? []).map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => send(item.question)}
                      className="hit rounded-[8px] border border-[var(--color-rule-strong)] px-4 py-3 text-left text-[15px] leading-snug"
                    >
                      {item.question}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

      {submit.isError && (
        <p role="alert" className="mb-4 text-[13px] text-[var(--color-defect)]">
          {(submit.error as Error).message}
        </p>
      )}

      {/*
        The answer arrives after a pause and replaces nothing on screen, so
        without this a screen reader user is told nothing at all — the page
        simply grows above them. Polite rather than assertive: it should not
        interrupt someone still reading the previous answer.
      */}
      <div aria-live="polite" aria-atomic="false">
        {submit.isPending && <p className="sr-only">Reading the agreement…</p>}
      </div>

      {history.map((result, index) => (
        /*
         * A conversation, at reading measure.
         *
         * The answer used to render at 13px sans across the full viewport —
         * on a wide screen that is a 200-character line, which is unreadable
         * for anything longer than a sentence and this is prose about
         * someone's pay. It is a passage of writing, so it gets the reading
         * register the document itself uses: serif, 16px, and a hard measure.
         */
        <article key={`${result.question}-${index}`} className="mx-auto mb-8 max-w-[680px]">
          {/*
            The question, as the thing you said. Right-aligned and bounded so
            the exchange reads as a conversation rather than as a heading
            followed by a report.
          */}
          <div className="mb-5 flex justify-end">
            <p className="max-w-[80%] rounded-[8px] border border-[var(--color-rule-strong)] px-3.5 py-2.5 text-[14px] leading-snug">
              {result.question}
            </p>
          </div>

          {result.refusal ? (
            /*
             * Given the same weight as an answer. A refusal here is usually
             * the software having caught something, and styling it as an
             * error teaches people to retry until they get prose — which is
             * precisely the failure the guards exist to prevent.
             */
            <div className="rounded-[6px] border border-[var(--color-defect)] px-3 py-2">
              <p className="label mb-1 text-[11px] text-[var(--color-defect)]">
                Not answered
              </p>
              <p className="measure-wide text-[13px] leading-relaxed">{result.refusal}</p>
            </div>
          ) : (
            <>
              {/* Paragraphs and tables. Splitting on blank lines was enough
                  while the prompt forbade structure; a comparison rendered
                  that way printed its pipes down the middle of the answer. */}
              <div className="register-reading">
                {parseAnswer(result.answer).map((block, i) =>
                  block.kind === 'paragraph' ? (
                    <p key={i} className="mb-4 last:mb-0">
                      {block.text}
                    </p>
                  ) : (
                    <AnswerTable key={i} header={block.header} rows={block.rows} />
                  ),
                )}
              </div>

              {result.evidence.length > 0 && (
                <div className="mt-5 rounded-[8px] border border-[var(--color-rule)]">
                  <p className="label border-b border-[var(--color-rule)] px-3 py-2">
                    The exact words
                  </p>
                  {result.evidence.map((card) => (
                    <div
                      key={`${card.instrument}-${card.number}`}
                      className="flex gap-3 border-b border-[var(--color-rule)] px-3 py-2.5 last:border-b-0"
                    >
                      {/*
                        Only our own clauses link. Read mode holds the
                        agreement alone, so linking a comparator's number opens
                        a different instrument's provision or nothing at all.
                      */}
                      {/* The number in its own gutter, as the document sets
                          it — so the eye finds the citation without reading. */}
                      <div className="w-14 shrink-0 pt-0.5 text-right">
                        {card.isPrimary ? (
                          <button
                            type="button"
                            onClick={() => onOpenClause(card.number)}
                            className="clause-number text-[12px] underline decoration-[var(--color-rule-strong)] underline-offset-2"
                          >
                            {card.number}
                          </button>
                        ) : (
                          <span className="clause-number text-[12px]">{card.number}</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="label mb-0.5">
                          {card.instrument}
                          {card.pageStart ? ` · p${card.pageStart}` : ''}
                        </p>
                        {/* Verbatim, so the answer can be checked where it is read. */}
                        <p className="register-reading text-[14px] leading-relaxed">{card.text}</p>
                      </div>
                    </div>
                  ))}


                </div>
              )}

              {result.citations.length === 0 && (
                <p className="mt-2 text-[12px] text-[var(--color-muted)]">
                  No clause was cited, which usually means the agreement is silent on this. A
                  silence is worth raising — check Compare to see whether another instrument covers
                  it.
                </p>
              )}

              {/*
                The action that closes the loop. Until this existed, the note
                above told a representative a silence was worth raising and gave
                them nowhere to raise it — advice with no affordance, which is
                the shape of half the defects found in this app.
              */}
              <ClaimFromAnswer answer={result} turnId={turnIds[result.question]} />
            </>
          )}
        </article>
      ))}

      {/*
        The question, immediately, with something moving under it.

        Same bubble the answered exchange uses, so when the answer arrives the
        question does not jump or restyle — it simply gains a reply.

        A sibling of the answers, not a child of one. Flipping the transcript to
        read downwards moved this below the map, and it landed inside the map's
        callback — the closing brace it was placed after belonged to the
        evidence cards rather than to the list. With an answer on screen the
        question and the thinking dots rendered inside the previous answer's
        card of quoted clauses; on an empty conversation the callback never ran,
        so a new question produced a blank column until the answer arrived. One
        misplaced insertion, two bugs that looked unrelated.
      */}
      {pending ? (
        <article className="mx-auto mb-8 max-w-[680px]">
          <div className="mb-5 flex justify-end">
            <p className="max-w-[80%] rounded-[8px] border border-[var(--color-rule-strong)] px-3.5 py-2.5 text-[14px] leading-snug">
              {pending}
            </p>
          </div>
          <p className="label mb-2" role="status" aria-live="polite">
            Reading the agreement
          </p>
          <span className="thinking" aria-hidden>
            <i />
            <i />
            <i />
          </span>
        </article>
      ) : null}
        </div>

        {/*
          Pinned to the bottom, where the mockup puts it and where a chat-shaped
          surface expects it. The old form sat under the heading, above the
          answers, so every reply pushed the thing you type into further away.
        */}
        <div className="shrink-0 border-t border-[var(--color-rule)] px-6 py-4">
          {/*
            A composer, not a search field.
            
            It was a single-line input the height of a button, which is the
            right shape for a query and the wrong one for what people actually
            type here — the questions in this thread run to two and three
            lines, and a box that shows fourteen words of them hides most of
            the question while it is being written.
            
            So: a textarea that starts at three lines and grows to about ten
            before it scrolls, with the controls on a footer row inside the
            same border. The border moves to the whole composer rather than
            the field, which is also what makes it read as the primary thing
            on the screen.
          */}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              send(question);
            }}
            className="mx-auto max-w-[680px]"
          >
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const files = [...e.dataTransfer.files];
                if (files.length > 0) attach.mutate(files);
              }}
              className="rounded-[10px] border px-3 pb-2 pt-2.5"
              style={{
                borderColor: dragging ? 'var(--color-accent, var(--color-ink))' : 'var(--color-ink)',
                background: dragging ? 'var(--color-subtle)' : undefined,
              }}
            >
              {/* What is already attached, and what is arriving. */}
              {(attached.data?.attachments.length ?? 0) > 0 || attach.isPending ? (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {(attached.data?.attachments ?? []).map((a) => (
                    <span
                      key={a.id}
                      className="label flex items-center gap-1.5 rounded-[6px] border border-[var(--color-rule-strong)] px-2 py-1 text-[10.5px]"
                      title={
                        a.truncated
                          ? `Only the first part of this ${a.chars.toLocaleString()} character document was read`
                          : `${a.chars.toLocaleString()} characters`
                      }
                    >
                      {a.filename}
                      {a.truncated ? ' · part read' : ''}
                      <button
                        type="button"
                        onClick={() => detach.mutate(a.id)}
                        aria-label={`Remove ${a.filename}`}
                        className="hit text-[var(--color-muted)]"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                  {attach.isPending ? <span className="label text-[10.5px]">Reading…</span> : null}
                </div>
              ) : null}
              {/*
                A placeholder is not a label: it disappears on focus, so a
                screen reader announces an unnamed text box and anyone using
                magnification loses the only thing telling them what to type.
              */}
              <label htmlFor="ask-question" className="sr-only">
                Your question about the agreement
              </label>
              <textarea
                id="ask-question"
                ref={composer}
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={(event) => {
                  /*
                   * Enter sends, Shift+Enter breaks the line. That is the
                   * convention in every chat this group already uses, and a
                   * textarea without it makes the send button the only way
                   * out of a box people will type paragraphs into.
                   */
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    send(question);
                  }
                }}
                rows={3}
                placeholder="Ask about pay, hours, leave, or what happens if the agreement changes…"
                /*
                  No maxLength, deliberately.
                  
                  It was 500, and `maxLength` does not refuse a paste — it takes
                  the front of it and drops the rest without a word. Somebody
                  pasted two pages of another school's claims and sent five
                  hundred characters ending mid-sentence, which then produced a
                  bad answer that looked like the model's fault. The limit is
                  now shown as a count and enforced on send, so text a person
                  can see in the box is text that gets sent.
                */
                className="hit w-full resize-none bg-transparent text-[14px] leading-relaxed outline-none"
                style={{ minHeight: 66, maxHeight: 220 }}
              />

              <div className="mt-1 flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-3">
                  <label
                    title="Attach a PDF, text, markdown or CSV document"
                    className="hit shrink-0 cursor-pointer rounded-[6px] p-1 text-[var(--color-muted)] hover:text-[var(--color-ink)]"
                  >
                    <Paperclip />
                    {/*
                      The aria-label carries the whole name now, and is not
                      decoration.
                      
                      While the control read ATTACH, the wrapping label named it
                      implicitly and this was belt and braces. The icon replaced
                      that text, the svg is aria-hidden as a drawing should be,
                      and without this the control announces as an unnamed file
                      input — which is worse than the word it replaced.
                    */}
                    <input
                      type="file"
                      multiple
                      aria-label="Attach a PDF, text, markdown or CSV document to this conversation"
                      accept=".txt,.md,.csv,.pdf,text/plain,text/markdown,text/csv,application/pdf"
                      className="sr-only"
                      onChange={(e) => {
                        const files = [...(e.target.files ?? [])];
                        if (files.length > 0) attach.mutate(files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {/*
                    One line, and the overflow outranks the rest of it.
                    
                    A person who has just pasted more than fits needs to be told
                    now — while the text is still in the box and they can act on
                    it — rather than after a send that quietly dropped most of
                    it. It names the document upload because that is the actual
                    answer for anything this long, and it is already six inches
                    to the left.
                  */}
                  <span
                    className="label truncate text-[10.5px]"
                    style={{ color: over > 0 ? 'var(--color-ink)' : 'var(--color-muted)' }}
                  >
                    {over > 0
                      ? `${over.toLocaleString()} characters too long — trim it, or attach it as a document`
                      : attach.isError
                        ? (attach.error as Error).message
                        : nearLimit
                          ? `${question.trim().length.toLocaleString()} of ${MAX_QUESTION.toLocaleString()} characters`
                          : 'Enter to send · drop a PDF or text file to ask about it'}
                  </span>
                </div>
                <button
                  type="submit"
                  disabled={
                    submit.isPending ||
                    question.trim().length < 3 ||
                    question.trim().length > MAX_QUESTION
                  }
                  className="hit shrink-0 rounded-[6px] px-4 py-1.5 text-[13px] disabled:opacity-40"
                  style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
                >
                  {submit.isPending ? 'Reading…' : 'Ask'}
                </button>
              </div>
            </div>
          </form>

          <div className="mx-auto mt-2 flex max-w-[680px] items-baseline justify-between gap-4">
            {/*
              The grounding claim and the disclaimer in one line, because both
              are true of every answer and neither belongs in a tooltip. These
              are teachers deciding what to put to their employer.
            */}
            <p className="text-[11px] text-[var(--color-muted)]">
              Grounded in your agreement, the Award and the NSW public Award · not legal advice
            </p>
            {history.length > 0 ? (
              <button
                type="button"
                /*
                 * The same thing the rail's New button does, which it was not.
                 *
                 * This cleared `history` and left the writer holding the
                 * thread, so "start over" emptied the screen and then went on
                 * appending to the conversation it appeared to have ended. The
                 * rail's count climbed while the visible exchange stayed short,
                 * and a follow-up carried context from questions the person
                 * could no longer see.
                 */
                onClick={startNewThread}
                className="hit text-[11px] underline underline-offset-2 text-[var(--color-muted)]"
              >
                Start over
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/*
        What it can read, named.
        
        "Grounded in your agreement, the Award and the NSW public Award" is a
        claim, and a claim is worth less than the three documents listed with
        their status. It also does the harder job: saying out loud that a
        silence is a finding rather than a failure. People assume a tool that
        will not answer is broken, ask again in different words, and eventually
        believe the version that finally produced prose — which is exactly the
        behaviour the citation contract exists to prevent.
      */}
      <aside
        className="hidden w-[280px] shrink-0 overflow-y-auto border-l border-[var(--color-rule)] p-3 lg:block"
        aria-label="What this can read"
      >
        <p className="label mb-2">What it can read</p>
        {(instruments.data ?? []).map((doc) => (
          <div
            key={doc.shortName}
            className="mb-2 rounded-[6px] border border-[var(--color-rule)] p-2.5"
          >
            <p className="mb-1 text-[12.5px] font-medium leading-snug">{doc.shortName}</p>
            <p className="text-[11.5px] leading-snug text-[var(--color-muted)]">
              {doc.isPrimary
                ? `Your agreement — ${doc.clauses} clauses.`
                : 'A comparator. Read, not negotiated.'}
            </p>
            <p className="label mt-1.5">✓ full text loaded</p>
          </div>
        ))}
        <div className="rounded-[6px] border border-[var(--color-ink)] p-2.5">
          <p className="text-[11.5px] leading-snug">
            It will not answer from general knowledge. If the answer isn&rsquo;t in these
            documents, it says so — and that silence is usually the finding.
          </p>
        </div>
      </aside>
    </div>
  );
}

/**
 * A comparison, set as one.
 *
 * Figures in mono and right-aligned, labels in the text face, because the whole
 * reason a teacher asked for this is to see two numbers next to each other and
 * tell which is larger. §7 reserves the strong rule for a section boundary, so
 * the header sits on the light one.
 *
 * No column widths are set. The content decides them, and the content here is
 * short — "2,550 min" against "2,240 min" — so a fixed grid would strand the
 * numbers at opposite ends of the pane.
 */
function AnswerTable({ header, rows }: { header: string[]; rows: string[][] }) {
  // A cell that is entirely a figure is set in mono. Mixed text like
  // "51 per fortnight" stays in the reading face, because a sentence that
  // happens to open with a number is not a number.
  const isFigure = (cell: string): boolean => /^[\d.,$%+\-\u2212]+$/.test(cell.trim());

  return (
    <div className="mb-4 overflow-x-auto">
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr>
            {header.map((cell, i) => (
              <th
                key={i}
                scope="col"
                className={`label border-b border-[var(--color-rule-strong)] px-2 py-1.5 align-bottom ${
                  i === 0 ? 'text-left' : 'text-right'
                }`}
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td
                  key={c}
                  className={`border-b border-[var(--color-rule)] px-2 py-1.5 ${
                    c === 0 ? 'text-left' : 'text-right'
                  } ${isFigure(cell) ? 'clause-number' : ''}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The threads, yours and the ones shared with you.
 *
 * Grouped rather than merged. A thread the other representative shared is read
 * differently from your own — you did not choose its question and cannot add to
 * it — and a single list sorted by recency would put theirs above yours with
 * nothing but a name to tell them apart.
 *
 * The opening question is the title. Nothing asks anyone to name a thread,
 * because naming is work done for the tool rather than for the argument, and
 * the first question is what a person actually remembers about it.
 */
function ThreadRail({
  threads,
  activeId,
  onOpen,
  onShare,
}: {
  threads: AskThreadRow[];
  activeId: string | null;
  onOpen: (id: string) => void;
  onShare: (id: string, visibility: 'private' | 'team') => void;
}) {
  if (threads.length === 0) return null;

  const mine = threads.filter((t) => t.mine);
  const shared = threads.filter((t) => !t.mine);

  const group = (label: string, rows: AskThreadRow[], showAuthor: boolean) =>
    rows.length === 0 ? null : (
      <div className="border-b border-[var(--color-rule)] px-3 py-2.5">
        <p className="label mb-1.5">{label}</p>
        <div className="flex flex-col gap-1">
          {rows.map((t) => (
            <div key={t.id} className="flex items-start gap-1">
              <button
                type="button"
                onClick={() => onOpen(t.id)}
                aria-current={t.id === activeId}
                className="hit min-w-0 flex-1 rounded-[6px] px-2 py-1.5 text-left text-[11.5px] leading-snug"
                style={t.id === activeId ? { background: 'var(--color-subtle)' } : undefined}
              >
                <span className="block truncate">{t.label}</span>
                <span className="label mt-0.5 block text-[10.5px]">
                  {showAuthor ? `${t.author} · ` : ''}
                  {t.turns} {t.turns === 1 ? 'question' : 'questions'}
                  {t.claims > 0 ? ` · ${t.claims} claimed` : ''}
                </span>
              </button>

              {/*
                Sharing is the author's alone, so the control only appears on
                their own threads. Showing it disabled on somebody else's would
                imply the permission exists and is merely unavailable.
              */}
              {t.mine ? (
                <button
                  type="button"
                  onClick={() => onShare(t.id, t.visibility === 'team' ? 'private' : 'team')}
                  title={
                    t.visibility === 'team'
                      ? 'Shared with the other representative. Click to make it private again.'
                      : 'Private to you. Click to share it with the other representative.'
                  }
                  aria-label={t.visibility === 'team' ? 'Make private' : 'Share with the team'}
                  aria-pressed={t.visibility === 'team'}
                  className="hit label shrink-0 rounded-[6px] px-1.5 py-1.5 text-[10.5px]"
                >
                  {/* ⇄ is in the §7 glyph table. Shared reads as ink, private
                      as muted, so the state is legible without the tooltip. */}
                  <span
                    style={{
                      color: t.visibility === 'team' ? 'var(--color-ink)' : 'var(--color-muted)',
                    }}
                  >
                    ⇄
                  </span>
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    );

  return (
    <>
      {group('Your chats', mine, false)}
      {group('Shared with you', shared, true)}
    </>
  );
}

/**
 * A paperclip, drawn rather than typed.
 *
 * The control was the word ATTACH, underlined, sitting beside the hint text —
 * two pieces of small caps competing for the same corner, and the one that is
 * a button looked like the one that is not.
 *
 * Drawn as an outline because it is the only way to have one here. The glyph
 * guard forbids Extended_Pictographic characters in components, which is what
 * the paperclip emoji is, and rightly: an emoji is a colour picture that does
 * not take the surrounding weight and does not survive a print. An inline
 * stroke does both — currentColor means it inherits the muted-to-ink hover the
 * rest of the surface uses, and 1.25 matches the rule weight beside it.
 *
 * No icon library for one icon. A dependency to draw twelve pixels is a
 * dependency to audit, update and eventually remove.
 */
function Paperclip() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}
